import { autoinject, BindingEngine, computedFrom, transient, Disposable } from "aurelia-framework";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { AccessRequestModel, AccessTypeEnum, DocumentManagementEntityTypeEnum, GetCustomerNamesAccessResponse, GiveDocumentAccessRequest, GrantTypeEnum, SubscriptionAccessModel, UploadDocumentRequest } from '../../../models/document-management.models';
import { DocumentAccessApi } from '../../../apis/document-access.api';
import { DocumentManagementApi } from '../../../apis/document-management.api';
import { LookupClientService } from '@rightship-tech/common-ui/services/lookups/lookup-client.service';
import { LookupValueRequest } from '@rightship-tech/common-ui/models/lookup.models';
import Enumerable from 'linq';
import { NotificationClientService, NotificationType } from '@rightship-tech/common-ui/services/basic/notification-client.service';
import { ValidationController, ValidationControllerFactory, ValidationRules } from 'aurelia-validation';
import { CustomValidationRenderer } from '@rightship-tech/common-ui/client-validation-system/custom-validation-renderer';
import { NotifyPropertyChangedBase, Reflection } from "@rightship-tech/common-ui/helpers/reflection";
import { GoogleAnalyticsService } from "@rightship-tech/common-ui/services/analytics/google-analytics.service";

@autoinject
@transient()
export class DialogManageAccess extends NotifyPropertyChangedBase {
    private subscriptions: Disposable[] = [];

    private _documentId: number;
    public documentTypeId: number;
    public documentTypeName: string;
    public vesselId: number;
    public vesselName: string;
    public vesselIdeintifier: string;
    public isEditing: boolean;
    public isExpired: boolean;
    public accessRules: SubscriptionAccessModel[] = [];
    public accessRequests: AccessRequestModel[] = [];
    public documents: File[] = [];
    public authorize: boolean;
    public confirm: boolean;
    public selectedDefaultRuleId: number;
    public accessTypes: { id: number, name: string }[] = [
        { id: <number>AccessTypeEnum.DenyAccess, name: 'Deny Access' },
        { id: <number>AccessTypeEnum.AccessOnDemand, name: 'Access On Demand' },
        { id: <number>AccessTypeEnum.AccessAllowed, name: 'Access Allowed' }
    ];
    public grantTypes: { id: number, name: string }[] = [
        { id: <number>GrantTypeEnum.Approve, name: 'Approved' },
        { id: <number>GrantTypeEnum.Reject, name: 'Rejected' }
    ];
    public requestingParties: GetCustomerNamesAccessResponse[] = [];
    public issueDate: Date = null;
    public expiryDate: Date = null;
    public expiryMinDate: Date;
    private refreshData: () => void;

    public extensions: string = "JPG/JPEG, PNG, PDF, DOC, XLS";
    public acceptTypes: string = "image/jpg,image/jpeg,image/png,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    private _validator: ValidationController;
    private _tooltipIconHoverTimeout: any;
    private refressOnClose: boolean = false;

    @computedFrom(Reflection.getPropertyPath<DialogManageAccess>(x => x.accessRequests),
        Reflection.getPropertyPath<DialogManageAccess>(x => x.accessRequests.length),
        Reflection.getPropertyPath<DialogManageAccess>(x => x.changes.accessRequests)
    )
    public get pendingAccessRequests()
    {
        return this.accessRequests.filter(x => x.grantTypeId == GrantTypeEnum.Requested);
    }

    @computedFrom(Reflection.getPropertyPath<DialogManageAccess>(x => x.accessRequests),
        Reflection.getPropertyPath<DialogManageAccess>(x => x.accessRequests.length),
        Reflection.getPropertyPath<DialogManageAccess>(x => x.changes.accessRequests)
    )
    public get reviewedAccessRequests()
    {
        return this.accessRequests.filter(x => x.grantTypeId != GrantTypeEnum.Requested);
    }

    constructor(public dialogClientService: DialogClientService, public documentAccessApi: DocumentAccessApi,
        public documentManagementApi: DocumentManagementApi, public lookupService: LookupClientService,
        public notificationService: NotificationClientService, validationControllerFactory: ValidationControllerFactory,
        private bindingEngine: BindingEngine, private googleAnalyticsService: GoogleAnalyticsService) {
        super();

        this._validator = validationControllerFactory.createForCurrentScope();
        this._validator.addRenderer(new CustomValidationRenderer());
        this.setValidationRules();
        this.authorize = false;
        this.confirm = false;

        this.expiryMinDate = new Date();
        this.expiryMinDate.setHours(0, 0, 0, 0);
    }

    activate(model: { documentId: number, documentTypeId: number, vesselId: number, vesselName: string, vesselIdeintifier: string, defaultAccessTypeId: number, isEditing: boolean, isExpired: boolean, refreshData: () => void }) {
        this._documentId = model.documentId;
        this.documentTypeId = model.documentTypeId;
        this.vesselId = model.vesselId;
        this.vesselName = model.vesselName;
        this.vesselIdeintifier = model.vesselIdeintifier;
        this.selectedDefaultRuleId = model.defaultAccessTypeId;
        this.isEditing = model.isEditing;
        this.isExpired = model.isExpired;
        this.refreshData = model.refreshData;
    }

    async attached() {

        try {
            const attachmentTypesLookup = await this.lookupService.getLookup(new LookupValueRequest('AttachmentTypes', `${DocumentManagementEntityTypeEnum.DocumentRequirementSubscriptionVessel}, false`));
            this.documentTypeName = Enumerable.from(attachmentTypesLookup.activeValues).firstOrDefault(x => x.id == this.documentTypeId)?.name;
            this.requestingParties = (await this.documentAccessApi.getCustomerNamesForDocumentsAccess({ documentTypeId: this.documentTypeId, vesselId: this.vesselId }))
                .filter(x => x.customerName != "RightShip");
            await this.loadAccessRules();
        }
        catch (error) {
            console.error(error);
        }
    }

    public get getAccessRuleCount() {
        return this.accessRules.filter(x => x.accessTypeId).length;
    }

    private warningMessageChange: boolean = false;
    @computedFrom("selectedDefaultRuleId", "getAccessRuleCount", "warningMessageChange")
    public get getWarningMessage() {
        if (this.getAccessRuleCount == 0) {
            if (this.selectedDefaultRuleId == AccessTypeEnum.DenyAccess)
                return "Warning: According to the current access rules, no one, except RightShip, can view and access this document. To allow access, either modify the default settings above or add a new access rule.";
            if (this.selectedDefaultRuleId == AccessTypeEnum.AccessOnDemand)
                return "Warning: According to the current access rules, no one, except RightShip, can view and access this document without requesting access. To change access, either modify the default settings above or add a new access rule.";
            if (this.selectedDefaultRuleId == AccessTypeEnum.AccessAllowed)
                return "Warning: According to the current access rules, everyone can view and access this document. To change access, either modify the default settings above or add a new access rule.";
        } else {
            const accessAllowedCount = this.accessRules.filter(x => x.accessTypeId == AccessTypeEnum.AccessAllowed).length;
            const accessOnDemandCount = this.accessRules.filter(x => x.accessTypeId == AccessTypeEnum.AccessOnDemand).length;
            const denyAccessCount = this.accessRules.filter(x => x.accessTypeId == AccessTypeEnum.DenyAccess).length;

            let message = "Warning: According to the current access rules, ";

            if (accessAllowedCount > 0) {
                message += `${accessAllowedCount} requesting part${accessAllowedCount > 1 ? 'ies have' : 'y has'} access`;
            }

            var requestingPartyMessage = "";
            if (accessOnDemandCount > 0) {
                if (accessAllowedCount > 0) {
                    message += ", and ";
                    requestingPartyMessage = accessOnDemandCount > 1 ? 'others' : 'other';
                } else {
                    requestingPartyMessage = accessOnDemandCount > 1 ? 'requesting parties' : 'requesting party';
                }
                message += `${accessOnDemandCount} ${requestingPartyMessage} can see the document listed but cannot access unless they request access`;
            }

            if (denyAccessCount > 0) {
                if (accessAllowedCount > 0 || accessOnDemandCount > 0) {
                    message += ", and ";
                    requestingPartyMessage = denyAccessCount > 1 ? 'others' : 'other';
                } else {
                    requestingPartyMessage = denyAccessCount > 1 ? 'requesting parties' : 'requesting party';
                }
                message += `${denyAccessCount} ${requestingPartyMessage} cannot view or access the document`;
            }

            message += ".";
            return message;
        }
    }

    private async loadAccessRules() {
        if (!this._documentId || this._documentId <= 0)
            return;
        const response = await this.documentAccessApi.getDocumentAccess({ vesselDocumentId: this._documentId });
        this.accessRules = response?.subscriptionAccesses;
        this.accessRequests = response?.accessRequests;

        this.accessRules.forEach((rule: SubscriptionAccessModel) => {
            const sub: Disposable = this.bindingEngine
                .propertyObserver(rule, "accessTypeId")
                .subscribe(() => { this.warningMessageChange = !this.warningMessageChange; });
            this.subscriptions.push(sub);
        });

        

        this.accessRequests.forEach((a: AccessRequestModel) => {
            a["element"] = null;
            const sub: Disposable = this.bindingEngine
                .propertyObserver(a, "element")
                .subscribe(() => { 
                    this.setIsLongText(a);
                 });
            this.subscriptions.push(sub);
        });
    }

    detached() {
        this.subscriptions?.forEach(x => x.dispose());
    }

    //Close
    public close() {
        if (this.refressOnClose && this.refreshData) {
            this.refreshData();
        }

		//Send event to google analytics
		this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_upload_docs_manage_access_cancel", {});

        //Hide dialog
        this.dialogClientService.hide(true);
    }

    //Add another access rule
    public addAnotherAccessRule() {
        if (!this.accessRules)
            this.accessRules = [];
        const newRule: SubscriptionAccessModel = {
            documentRequirementId: null,
            accessTypeId: null
        };
        this.accessRules.push(newRule);
        const sub: Disposable = this.bindingEngine
            .propertyObserver(newRule, "accessTypeId")
            .subscribe(() => { this.warningMessageChange = !this.warningMessageChange; });
        this.subscriptions.push(sub);

		//Send event to google analytics
		this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_upload_docs_manage_access_add_rule", {});
    }

    //Delete access rule
    public deleteAccessRule(index: number) {
        this.accessRules?.splice(index, 1);
    }

    public deleteDocument(index: number) {
        this.documents?.splice(index, 1);
    }

    //Set rules for validations
    private setValidationRules() {
        ValidationRules
            .ensure<DialogManageAccess, any>(x => x.documents)
            .required()
            .minItems(1)
            .withMessage("At least 1 document is required")
            .ensure(x => x.authorize)
            .required()
            .equals(true)
            .withMessage("Authorization to Rightship is required")
            .ensure(x => x.confirm)
            .required()
            .equals(true)
            .withMessage("Accurate information acknowledgement is required")
            .on(this);
    }

    public async save() {
        var submissionReady: boolean = false;
        try {
            if (this._documentId > 0 && this.documents.length == 0) {
                await this.documentAccessApi.saveDocumentAccess({ vesselDocumentId: this._documentId, defaultAccessTypeId: this.selectedDefaultRuleId, access: this.accessRules });
                this.notificationService.show('Access Rules Updated Successful', '', NotificationType.Success);
                submissionReady = true;
            }
            else {
                const request: UploadDocumentRequest = {
                    access: this.accessRules,
                    accurateInfoAcknoledgement: this.confirm,
                    documentTypeId: this.documentTypeId,
                    defaultAccessTypeId: this.selectedDefaultRuleId,
                    name: this.documentTypeName,
                    vesselId: this.vesselId,
                    expiryDate: this.expiryDate,
                    issueDate: this.issueDate,
                    visibilityAuthorized: this.authorize,
                    files: this.documents,
                    expiredDocumentId: this.isExpired ? this._documentId : null
                };

                const validationResult = await this._validator.validate();
                if (validationResult.valid) {
                    await this.documentManagementApi.uploadDocument(request);
                    this.notificationService.show('Upload Successful', '', NotificationType.Success);
                    submissionReady = true;

                    //Send event to google analytics
                    this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_upload_docs_manage_access_save", {});
                }
            }
        } catch (error) {
            console.error(error);
            this.notificationService.show('Error', `An error occurred while saving the ${this._documentId > 0 && !this.isExpired ? 'access rules' : 'document'}.`, NotificationType.Error);
        }
        finally {
            if (this.refreshData)
                this.refreshData();
            if (submissionReady)
                this.dialogClientService.hide();
        }
    }

    setIsLongText(request: AccessRequestModel)
    {
        const element = request? request["element"]: null;
        const comments = request?.requestComments?.replaceAll("\n", "<br/>");
        if (element && comments && element?.innerHTML?.trim() != '')
        {
            var div = document.createElement('div');
            div.innerHTML = comments;
            div.style.visibility = 'hidden';
            element.parentElement.appendChild(div)
            const style = window.getComputedStyle(div);
            const lineHeight = parseFloat(style.lineHeight?.replace('px', ''));
            const height = parseFloat(style.height?.replace('px', ''));
            const lines = Math.round(height / lineHeight);
            request["isLongText"] = lines > 2;
            element.parentElement.removeChild(div);
        }
    }
    
    getComments(element: Element, comments: string, showLess: boolean = false)
    {
        comments = comments?.replaceAll("\n", "<br/>");

        return comments;
    }

    toggleShowMore(request)
    {
        if(request)
        {
            request.isShowLess = !request.isShowLess;
        }
    }

    public async denyAccess(request: AccessRequestModel) {
        await this.giveAccess(request, GrantTypeEnum.Reject);
    }

    public async acceptAccess(request: AccessRequestModel) {
        await this.giveAccess(request, GrantTypeEnum.Approve);
    }

    private async giveAccess(request: AccessRequestModel, grant: GrantTypeEnum)
    {
        try
        {
            request["isLoading"] = true;
            const accessRequest: GiveDocumentAccessRequest = {
                documentAccessRequestId: request.accessRequestId,
                grant: grant,
                itemName: this.documentTypeName,
                vesselImo: this.vesselIdeintifier,
                vesselName: this.vesselName
            };
            await this.documentAccessApi.giveDocumentAccess(accessRequest);
            this.notificationService.show(`Access ${grant == GrantTypeEnum.Approve? 'Granted': 'Denied'}`, '', NotificationType.Success);
            request["isLoading"] = false;
            this.refressOnClose = true;
            request.grantTypeId = grant;
            this.onPropertyChanged<DialogManageAccess>(x => x.accessRequests);
        }
        catch (error)
        {
            console.error(error);
            request["isLoading"] = false;
        }
    }

    public async accessGrantTypeChanged(request: AccessRequestModel, detail: { id: number, name: string })
    {
        if(!request || !detail || !(detail.id > 0) || detail?.id == request?.grantTypeId) return;

        await this.giveAccess(request, detail?.id);
    }

    //Sent GA4 event after 400ms of hover
    public tooltipIconMouseOver() {
        this._tooltipIconHoverTimeout = setTimeout(() => {
            this.googleAnalyticsService.sendCustomGA4EventMulti("dmc_upload_docs_manage_access_info", {});
        }, 400);
    }

    //If the mouse leaves before 400ms, clear the timeout
    public tooltipIconMouseOut() {
        clearTimeout(this._tooltipIconHoverTimeout);
    }
}

